import { Chip, makeStyles, Menu, MenuItem, Tooltip } from '@material-ui/core';
import React, { useEffect, useState } from 'react';

import { ILiveStreamWatcherAdBreakData } from '~/src/dash/api/live_logs/models';
import {
  flagLiveStreamWatcherAdBreak,
  getFlagLiveStreamStatus,
} from '~/src/dash/api/live_logs/stream';
import { IconText } from '~/src/dash/components/IconText';
import { Col, Cols, Row, Rows, spacing, Span, Text } from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';

import { useBaseStore } from '../views/base/contexts';

const garmScoreFullNames: { [key: string]: string } = {
  S: 'Safe',
  L: 'Low',
  M: 'Medium',
  H: 'High',
  F: 'Floor',
};

const garmScoreColors: { [key: string]: { backgroundColor: string; color: string } } = {
  S: { backgroundColor: publicaColors.lighterGray, color: publicaColors.darkGray },
  L: { backgroundColor: publicaColors.lighterYellow, color: publicaColors.darkGoldenRod },
  M: { backgroundColor: publicaColors.lightOrange, color: publicaColors.darkGoldenRod },
  H: { backgroundColor: publicaColors.veryLightRed, color: publicaColors.maroon },
  F: { backgroundColor: publicaColors.lighterGray, color: publicaColors.darkGray },
};

const useStyles = makeStyles({
  clickableChip: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: `${publicaColors.veryLightRed} !important`,
    },
  },
  flaggedChip: {
    backgroundColor: `${publicaColors.veryLightRed} !important`,
  },
});

interface IProps {
  row: ILiveStreamWatcherAdBreakData;
  publisherID: string;
  siteID: number;
  isDirect?: boolean;
}

export const GarmScoreChips = ({
  row,
  publisherID,
  siteID,
  isDirect,
}: IProps): React.ReactElement => {
  const classes = useStyles();
  const [flaggedFields, setFlaggedFields] = useState<string[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedChipLabel, setSelectedChipLabel] = useState<string>('');
  const [corrections, setCorrections] = useState<{ [key: string]: string }>({});
  const { user } = useBaseStore();
  const userName = user.user.Username;

  useEffect(() => {
    const fetchFlagStatus = async (): Promise<void> => {
      const result = await getFlagLiveStreamStatus(publisherID, [row.url]);
      if (result && result.length > 0 && result[0]) {
        const fields = result[0].flaggedFields.split(',');
        setFlaggedFields(fields);
        const correctionJsonString = result[0].corrections;
        if (correctionJsonString) {
          try {
            const parsed = JSON.parse(correctionJsonString) as { [key: string]: string };
            const correctionJson: { [key: string]: string } = parsed || {};
            setCorrections(correctionJson);
          } catch (error) {
            console.error('Error parsing corrections JSON:', error);
            setCorrections({});
          }
        }
      }
    };
    fetchFlagStatus().catch((error) => {
      console.error('Error fetching flag status:', error);
    });
  }, [row.url, publisherID]);

  const handleChipClick = async (label: string): Promise<void> => {
    try {
      if (flaggedFields.includes(label)) {
        await flagLiveStreamWatcherAdBreak(
          publisherID,
          siteID,
          row.url,
          row.garmScores,
          row.personality,
          flaggedFields.filter((field) => field !== label),
          JSON.stringify(corrections),
          userName,
        );
        setFlaggedFields((prev) => prev.filter((field) => field !== label));
      } else {
        await flagLiveStreamWatcherAdBreak(
          publisherID,
          siteID,
          row.url,
          row.garmScores,
          row.personality,
          [...flaggedFields, label],
          JSON.stringify(corrections),
          userName,
        );
        setFlaggedFields((prev) => [...prev, label]);
      }
    } catch (error) {
      console.error('Error flagging category:', error);
    }
  };

  const handleDetectedChipClick = (event: React.MouseEvent<HTMLElement>, label: string): void => {
    setAnchorEl(event.currentTarget);
    setSelectedChipLabel(label);
  };

  const handleDropdownClose = (): void => {
    setAnchorEl(null);
    setSelectedChipLabel('');
  };

  const handleDropdownSelect = async (selectedValue: string): Promise<void> => {
    const newCorrections = corrections;
    if (corrections[selectedChipLabel]) {
      newCorrections[selectedChipLabel] = garmScoreFullNames[selectedValue];
    } else {
      newCorrections[selectedChipLabel] = garmScoreFullNames[selectedValue];
    }
    setCorrections(newCorrections);
    await flagLiveStreamWatcherAdBreak(
      publisherID,
      siteID,
      row.url,
      row.garmScores,
      row.personality,
      flaggedFields,
      JSON.stringify(newCorrections),
      userName,
    );
    // TODO: Implement the logic to update the chip with the selected value
    handleDropdownClose();
  };

  const parseGarmScores = (garmScores: string): { [key: string]: string[] } => {
    const scores = garmScores.split(',');
    const result: { [key: string]: string[] } = {};
    scores.forEach((score) => {
      const parts = score.split(':');
      if (parts.length === 2) {
        const name = parts[0];
        const value = parts[1];
        if (!result[value]) {
          result[value] = [];
        }
        result[value].push(name);
      }
    });
    return result;
  };

  const scoreGroups = parseGarmScores(isDirect ? row.garmScoresDirect : row.garmScores);

  return (
    <Rows>
      {Object.keys(scoreGroups).map((key) => {
        const categories = scoreGroups[key];
        return (
          <Row key={key} noPadding marginLeft='1em' marginTop='0.2em'>
            <Cols>
              <Col>
                <Text small>
                  <Span bold>
                    {' '}
                    {garmScoreFullNames[key] === 'Safe'
                      ? 'Safe:'
                      : `${garmScoreFullNames[key]} Risk:`}
                  </Span>
                </Text>
              </Col>
              <Col>
                {categories.map((label) => {
                  const isFlagged = flaggedFields.includes(label);
                  const labelElement = (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <IconText
                        bold
                        small
                        gap={spacing.extraSmall}
                        icon='flag'
                        color={isFlagged ? publicaColors.red : publicaColors.black}
                        text=''
                        style={{
                          padding: 'none',
                          opacity: isFlagged ? 1 : 0.5,
                        }}
                      />
                      <Text small>{label}</Text>
                    </div>
                  );
                  return (
                    <>
                      {isDirect ? (
                        <Chip
                          style={{
                            ...garmScoreColors[key],
                            margin: '0 0 0.3em 0.3em',
                          }}
                          label={label}
                          size='small'
                        />
                      ) : garmScoreFullNames[key] === 'Safe' ? (
                        <Tooltip
                          key={label}
                          title={isFlagged ? `Click to unflag ${label}` : `Click to flag ${label}`}
                          placement='top'
                        >
                          <Chip
                            className={`${isFlagged ? classes.flaggedChip : classes.clickableChip}`}
                            style={{
                              ...garmScoreColors[key],
                              margin: '0 0 0.3em 0.3em',
                            }}
                            size='small'
                            label={labelElement}
                            onClick={() => void handleChipClick(label)}
                            clickable
                          />
                        </Tooltip>
                      ) : (
                        <>
                          <Tooltip key={label} title={'Click to flag'} placement='top'>
                            <Chip
                              style={{
                                ...garmScoreColors[key],
                                margin: '0 0 0.3em 0.3em',
                              }}
                              size='small'
                              label={label}
                              onClick={(event) => handleDetectedChipClick(event, label)}
                              clickable
                            />
                          </Tooltip>
                        </>
                      )}
                    </>
                  );
                })}
              </Col>
            </Cols>
          </Row>
        );
      })}
      {Object.keys(corrections).length > 0 && (
        <Row noPadding marginLeft='1em' marginTop='0.5em'>
          <Cols>
            <Col>
              <Text small>
                <Span bold>Corrected: </Span>
              </Text>
            </Col>
            <Col>
              <div style={{ display: 'flex', gap: '0.3em', marginLeft: '0.3em' }}>
                {Object.entries(corrections).map(([category, score]) => (
                  <Chip
                    key={category}
                    size='small'
                    style={{
                      backgroundColor: publicaColors.veryLightRed,
                      color: publicaColors.lighterBlue,
                      fontSize: '0.60rem',
                    }}
                    label={
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Text extraSmall style={{ color: publicaColors.black }}>
                          {category}: {score}
                        </Text>
                      </div>
                    }
                  />
                ))}
              </div>
            </Col>
          </Cols>
        </Row>
      )}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleDropdownClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <MenuItem onClick={() => void handleDropdownSelect('S')}>Safe</MenuItem>
        <MenuItem onClick={() => void handleDropdownSelect('L')}>Low</MenuItem>
        <MenuItem onClick={() => void handleDropdownSelect('M')}>Medium</MenuItem>
        <MenuItem onClick={() => void handleDropdownSelect('H')}>High</MenuItem>
      </Menu>
    </Rows>
  );
};
